import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils";

type RulebookCardProps = {
  id: string;
  name: string;
  description?: string;
  numMeters: number;
  coverUrl?: string;
  isPrivate: boolean;
  /** `attached`: compact card in the owner's form; `selectable`: a row in the picker. */
  variant: "attached" | "selectable";
  isSelected?: boolean;
  onSelect?: (rulebookId: string) => void;
  /** Shown on public Rulebooks from Discover, as Lorebook cards show their creator. */
  creatorUsername?: string;
  /** Blur an NSFW cover for viewers who blur NSFW media. */
  isBlurred?: boolean;
  dataTestId?: string;
};

/** Cover, or a neutral tile: private Rulebooks get only the lock on top, as Lorebook cards do. */
const CoverImage = ({
  coverUrl,
  name,
  className,
  isPrivate,
  isBlurred = false,
}: {
  coverUrl?: string;
  name: string;
  className: string;
  isPrivate: boolean;
  isBlurred?: boolean;
}) => {
  const [stateIsImageError, setStateIsImageError] = useState(false);
  useEffect(() => setStateIsImageError(false), [coverUrl]);
  const handleError = useCallback(() => setStateIsImageError(true), []);
  if (!coverUrl || stateIsImageError) {
    return (
      <div className={cn("flex items-center justify-center bg-gray-5 text-gray-11", className)} aria-label={name}>
        {isPrivate ? null : <Icon icon={IconVariants.CHART_NO_AXES_COMBINED} size={IconSize.LARGE} />}
      </div>
    );
  }
  return <img src={coverUrl} alt={name} className={cn("object-cover", isBlurred && "blur-md", className)} onError={handleError} />;
};

const MeterCount = ({ numMeters }: { numMeters: number }) => {
  const values = useMemo(() => ({ count: numMeters }), [numMeters]);
  return (
    <span className="flex flex-none items-center gap-1 text-gray-10">
      <Icon icon={IconVariants.CHART_NO_AXES_COMBINED} size={IconSize.XSMALL} />
      <Text variant={TextVariants.LABEL_SMALL_REGULAR} as="span">
        <Trans i18nKey="rulebookEditor:attach.meters" defaults="{{count}} meters" values={values} />
      </Text>
    </span>
  );
};

/** A Rulebook as a card: cover, name, meter count and description — the Rulebook counterpart of `LorebookCard`. */
const RulebookCard = ({
  id,
  name,
  description,
  numMeters,
  coverUrl,
  isPrivate,
  variant,
  isSelected = false,
  onSelect,
  creatorUsername,
  isBlurred = false,
  dataTestId = "RulebookCard",
}: RulebookCardProps) => {
  const handleSelect = useCallback(() => onSelect?.(id), [onSelect, id]);

  if (variant === "attached") {
    return (
      <div data-testid={dataTestId} className="flex min-w-[300px] max-w-[500px] overflow-hidden rounded-[12px] bg-gray-3">
        <div className="relative h-[129px] w-[129px] flex-none">
          {isPrivate ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40">
              <Icon icon={IconVariants.LOCK} size={IconSize.MEDIUM} className="text-white" />
            </div>
          ) : null}
          <CoverImage coverUrl={coverUrl} name={name} className="size-full" isPrivate={isPrivate} isBlurred={isBlurred} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-[7px] py-[10px]">
          <Text variant={TextVariants.LABEL_LARGE_BOLD} className="truncate pr-6 text-gray-12">
            {name}
          </Text>
          <MeterCount numMeters={numMeters} />
          {description ? (
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="mt-1 line-clamp-3 text-gray-12">
              {description}
            </Text>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={dataTestId}
      onClick={handleSelect}
      aria-pressed={isSelected}
      className={cn(
        "group relative flex w-full cursor-pointer gap-md rounded-md border border-solid bg-gray-3 p-md text-left text-gray-12 hover:bg-gray-4 dark:text-white",
        isSelected ? "border-blue-10" : "border-gray-6 hover:border-gray-9/50 dark:hover:border-gray-8",
      )}
    >
      <div className="relative h-16 w-16 flex-none overflow-hidden rounded-md">
        {isPrivate ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/50">
              <Icon icon={IconVariants.LOCK} size={IconSize.MEDIUM} className="text-white opacity-90" />
            </div>
          </div>
        ) : null}
        <CoverImage coverUrl={coverUrl} name={name} className="size-full rounded-md" isPrivate={isPrivate} isBlurred={isBlurred} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-gray-11">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <Text variant={TextVariants.LABEL_MEDIUM_BOLD} className="min-w-0 truncate text-gray-12">
            {name}
          </Text>
          <span
            aria-hidden="true"
            className={cn(
              "h-4 w-4 flex-none rounded-full border border-solid",
              isSelected ? "border-[5px] border-blue-10 bg-white" : "border-gray-10 bg-gray-5 dark:border-none dark:bg-gray-9",
            )}
          />
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <MeterCount numMeters={numMeters} />
          {creatorUsername ? (
            <Text
              variant={TextVariants.LABEL_SMALL_REGULAR}
              as="span"
              className="min-w-0 truncate text-gray-10"
              data-testid={`${dataTestId}-Creator`}
            >
              @{creatorUsername}
            </Text>
          ) : null}
        </div>
        {description ? (
          <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="line-clamp-3 break-words">
            {description}
          </Text>
        ) : null}
      </div>
    </button>
  );
};

export default memo(RulebookCard);
